import { useEffect, useRef, useState } from "react";

const CLIPS = ["/media/broll-1.mp4", "/media/broll-2.mp4", "/media/broll-3.mp4"];

/**
 * Ambient wrestling b-roll behind the authenticated app. Fixed, muted,
 * autoplaying, and cycled through the clips; heavily obscured by the
 * `.app-bg-video-overlay` so it never hurts readability. Falls back to no
 * video (the static `.app-bg` image shows through) when the user prefers
 * reduced motion or autoplay is blocked.
 */
export function BgVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const [idx, setIdx] = useState(0);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce) setEnabled(false);
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!v || !enabled) return;
    v.muted = true;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Retry once shortly; only give up (static bg) if still blocked.
          setTimeout(() => {
            v.muted = true;
            const p2 = v.play();
            if (p2 && typeof p2.catch === "function")
              p2.catch(() => setEnabled(false));
          }, 300);
        });
      }
    };
    tryPlay();
  }, [idx, enabled]);

  if (!enabled) return null;

  return (
    <video
      ref={ref}
      className="app-bg-video"
      src={CLIPS[idx]}
      muted
      autoPlay
      playsInline
      preload="auto"
      // Cycle to the next clip when this one ends.
      onEnded={() => setIdx((i) => (i + 1) % CLIPS.length)}
      aria-hidden="true"
      tabIndex={-1}
    />
  );
}
